import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { listUsers } from '@/api/services/adminUsersService';
import type {
  AdminUserDto,
  UserRole,
  UserState,
} from '@/api/types/AdminUser';
import { USER_ROLES, USER_STATES } from '@/api/types/AdminUser';

import { BrandChip, StatusChip, type StatusKey } from '@/components/shell';
import { TableSkeleton } from '@/components/loading';
import { useAppDispatch } from '@/store/hooks';
import {
  activateUserAndInvalidate,
  deactivateUserAndInvalidate,
  resendInviteAndInvalidate,
} from '@/store/adminUsersSlice';

import { AdminUserEditor } from './AdminUserEditor';

const NEW_USER_SUB = '__new__';
const USERS_TABLE_COLUMN_WIDTHS = ['16%', '11%', '11%', '13%', '11%', '15%', '12%', '11%'];
const USERS_SKELETON_ROWS = 6;

const BAR_GAP_FACTOR = 1.5;
const BAR_PAD_Y_FACTOR = 1.5;

// User state → shared StatusChip palette key (DESIGN.md §2 status rows).
// AKTIVNI → `active` (DCFCE7/15803D — matches mockup `.user-state-dot.active`),
// DEAKTIVOVANY → `inactive` (grey, matches `.user-state-dot.disabled`),
// POZVANY → `waiting` (amber, matches the warning-toned `.user-state-dot.invited`).
const STATE_STATUS_KEY: Record<UserState, StatusKey> = {
  AKTIVNI: 'active',
  POZVANY: 'waiting',
  DEAKTIVOVANY: 'inactive',
};

// Per-row notifications on/off indicator (CHR-03 §1). Reuses the shared
// BrandChip: ON → primary-toned with the active-bell glyph, OFF → muted with
// the bell-off glyph. Muted-only when the row is deactivated so it never
// competes visually with the (then dominant) faded row.
const NotificationStateCell = ({
  enabled,
  deactivated,
  sub,
}: {
  enabled: boolean;
  deactivated: boolean;
  sub: string;
}): JSX.Element => {
  const { t } = useTranslation('admin');
  const variant = enabled && !deactivated ? 'brand' : 'muted';
  const Icon = enabled
    ? NotificationsActiveOutlinedIcon
    : NotificationsOffOutlinedIcon;
  return (
    <BrandChip
      variant={variant}
      data-testid={`admin-user-notif-${sub}`}
      className={enabled ? 'notif-on' : 'notif-off'}
    >
      <Icon sx={{ fontSize: 14 }} aria-hidden />
      {t(enabled ? 'notifState.on' : 'notifState.off')}
    </BrandChip>
  );
};

const matchesSearch = (u: AdminUserDto, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    u.email.toLowerCase().includes(needle) ||
    u.firstName.toLowerCase().includes(needle) ||
    u.lastName.toLowerCase().includes(needle)
  );
};

export const AdminUsersPage = (): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  );
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [stateFilter, setStateFilter] = useState<UserState | 'all'>('all');
  const [editorSub, setEditorSub] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setStatus('loading');
    try {
      const data = await listUsers();
      setUsers(data);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(
    () =>
      users.filter(
        (u) =>
          matchesSearch(u, search) &&
          (roleFilter === 'all' || u.roles.includes(roleFilter)) &&
          (stateFilter === 'all' || u.state === stateFilter),
      ),
    [users, search, roleFilter, stateFilter],
  );

  const editingUser = useMemo(
    () => (editorSub && editorSub !== NEW_USER_SUB
      ? users.find((u) => u.sub === editorSub) ?? null
      : null),
    [users, editorSub],
  );

  const handleSaved = useCallback(
    (saved: AdminUserDto, isCreate: boolean) => {
      setUsers((prev) => {
        const exists = prev.some((u) => u.sub === saved.sub);
        return exists
          ? prev.map((u) => (u.sub === saved.sub ? saved : u))
          : [...prev, saved];
      });
      enqueueSnackbar(t(isCreate ? 'toast.created' : 'toast.updated'), {
        variant: 'success',
      });
      setEditorSub(null);
    },
    [enqueueSnackbar, t],
  );

  const handleDeactivate = useCallback(
    async (user: AdminUserDto): Promise<void> => {
      try {
        const updated = await dispatch(
          deactivateUserAndInvalidate(user.sub),
        ).unwrap();
        setUsers((prev) => prev.map((u) => (u.sub === updated.sub ? updated : u)));
        enqueueSnackbar(t('toast.deactivated'), { variant: 'success' });
      } catch {
        enqueueSnackbar(t('toast.deactivateFailed'), { variant: 'error' });
      }
    },
    [dispatch, enqueueSnackbar, t],
  );

  const handleActivate = useCallback(
    async (user: AdminUserDto): Promise<void> => {
      try {
        const updated = await dispatch(
          activateUserAndInvalidate(user.sub),
        ).unwrap();
        setUsers((prev) => prev.map((u) => (u.sub === updated.sub ? updated : u)));
        enqueueSnackbar(t('toast.activated'), { variant: 'success' });
      } catch {
        enqueueSnackbar(t('toast.activateFailed'), { variant: 'error' });
      }
    },
    [dispatch, enqueueSnackbar, t],
  );

  const handleResendInvite = useCallback(
    async (user: AdminUserDto): Promise<void> => {
      try {
        await dispatch(resendInviteAndInvalidate(user.sub)).unwrap();
        enqueueSnackbar(t('toast.inviteResent'), { variant: 'success' });
      } catch {
        enqueueSnackbar(t('toast.inviteFailed'), { variant: 'error' });
      }
    },
    [dispatch, enqueueSnackbar, t],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
      data-testid="admin-users-page"
    >
      {/* Toolbar — search + role/state filters (mockup `.toolbar`); the
          „Pozvat nového uživatele" action moved here (right-aligned) when the
          page title/breadcrumb band was removed app-wide. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(BAR_GAP_FACTOR),
          pb: theme.spacing(BAR_PAD_Y_FACTOR),
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={t('filter.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280, flex: '0 1 360px' }}
          inputProps={{ 'data-testid': 'admin-users-search' }}
        />
        <TextField
          select
          size="small"
          label={t('filter.role')}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">{t('filter.roleAll')}</MenuItem>
          {USER_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {t(`role.${r}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('filter.state')}
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as UserState | 'all')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">{t('filter.stateAll')}</MenuItem>
          {USER_STATES.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`state.${s}`)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setEditorSub(NEW_USER_SUB)}
          data-testid="new-user-button"
          sx={{ ml: 'auto' }}
        >
          {t('action.newUser')}
        </Button>
      </Box>

      {/* Body: card-wrapped table or error pane */}
      {status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: theme.spacing(2) }}
          action={
            <Button color="inherit" size="small" onClick={() => void reload()}>
              {t('list.retry')}
            </Button>
          }
        >
          {t('list.loadFailed')}
        </Alert>
      ) : (
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3, // 12px (DESIGN.md §4 card radius — matches OrdersTable)
            boxShadow: theme.elevation.shadow1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('column.email')}</TableCell>
                  <TableCell>{t('column.firstName')}</TableCell>
                  <TableCell>{t('column.lastName')}</TableCell>
                  <TableCell>{t('column.phone')}</TableCell>
                  <TableCell>{t('column.notifications')}</TableCell>
                  <TableCell>{t('column.roles')}</TableCell>
                  <TableCell>{t('column.state')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((u) => {
                  const isDeactivated = u.state === 'DEAKTIVOVANY';
                  const isInvited = u.state === 'POZVANY';
                  const mutedText = isDeactivated
                    ? theme.palette.text.secondary
                    : undefined;
                  return (
                    <TableRow
                      key={u.sub}
                      hover
                      onClick={() => setEditorSub(u.sub)}
                      sx={{ cursor: 'pointer' }}
                      data-testid={`admin-user-row-${u.sub}`}
                      data-state={u.state}
                    >
                      <TableCell sx={{ fontWeight: 600, color: mutedText }}>
                        {u.email}
                      </TableCell>
                      <TableCell sx={{ color: mutedText }}>{u.firstName}</TableCell>
                      <TableCell sx={{ color: mutedText }}>{u.lastName}</TableCell>
                      <TableCell>
                        {u.phoneNumber ? (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: theme.monospaceFontFamily,
                              fontFeatureSettings: '"tnum"',
                              color: mutedText ?? theme.palette.text.primary,
                            }}
                          >
                            {u.phoneNumber}
                          </Box>
                        ) : (
                          <BrandChip variant="muted">{t('phoneEmpty')}</BrandChip>
                        )}
                      </TableCell>
                      <TableCell>
                        <NotificationStateCell
                          enabled={u.notificationsEnabled}
                          deactivated={isDeactivated}
                          sub={u.sub}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                          {u.roles.map((r) => (
                            <BrandChip
                              key={r}
                              variant={isDeactivated ? 'muted' : 'brand'}
                            >
                              {t(`role.${r}`)}
                            </BrandChip>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={STATE_STATUS_KEY[u.state]}
                          label={t(`state.${u.state}`)}
                          data-testid={`admin-user-state-${u.sub}`}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', width: 200 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Stack direction="row" gap={0.25} justifyContent="flex-end">
                          {isInvited ? (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => void handleResendInvite(u)}
                              data-testid={`resend-${u.sub}`}
                              sx={{ minHeight: 44 }}
                            >
                              {t('action.resendInvite')}
                            </Button>
                          ) : null}
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setEditorSub(u.sub)}
                            data-testid={`edit-${u.sub}`}
                            sx={{ minHeight: 44 }}
                          >
                            {t('action.edit')}
                          </Button>
                          {/* „Aktivovat" sits exactly where an active user
                              shows „Deaktivovat" — symmetric reactivation
                              (CHR-03 §3). Success-toned (positive) vs the
                              error-toned deactivate. */}
                          {isDeactivated ? (
                            <Button
                              variant="text"
                              size="small"
                              color="success"
                              onClick={() => void handleActivate(u)}
                              data-testid={`activate-${u.sub}`}
                              sx={{ minHeight: 44 }}
                            >
                              {t('action.activate')}
                            </Button>
                          ) : (
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => void handleDeactivate(u)}
                              data-testid={`deactivate-${u.sub}`}
                              sx={{ minHeight: 44 }}
                            >
                              {t('action.deactivate')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {status === 'ready' && filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        textAlign: 'center',
                        color: theme.palette.text.secondary,
                        py: theme.spacing(4),
                      }}
                    >
                      {t('list.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
                {status === 'loading' ? (
                  <TableSkeleton
                    rows={USERS_SKELETON_ROWS}
                    columnWidths={USERS_TABLE_COLUMN_WIDTHS}
                  />
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination footer (mockup `.tbl-pagination`) — client-side list,
              so it reports the shown/total count without page controls. */}
          {status === 'ready' && filtered.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: theme.spacing(2),
                py: theme.spacing(1.25),
                borderTop: `1px solid ${theme.palette.divider}`,
                fontSize: 12,
                color: theme.palette.text.secondary,
              }}
              data-testid="admin-users-pagination"
            >
              <Typography component="span" sx={{ fontSize: 12 }}>
                <Trans
                  i18nKey="admin:pagination.shown"
                  values={{ shown: filtered.length, total: users.length }}
                  components={[
                    <Box
                      key="shown"
                      component="b"
                      sx={{ fontVariantNumeric: 'tabular-nums', color: theme.palette.text.primary }}
                    />,
                    <Box
                      key="total"
                      component="b"
                      sx={{ fontVariantNumeric: 'tabular-nums', color: theme.palette.text.primary }}
                    />,
                  ]}
                />
              </Typography>
            </Box>
          ) : null}
        </Box>
      )}

      <AdminUserEditor
        sub={editorSub}
        existing={editingUser}
        onClose={() => setEditorSub(null)}
        onSaved={handleSaved}
        onDeactivated={(updated) => {
          setUsers((prev) =>
            prev.map((u) => (u.sub === updated.sub ? updated : u)),
          );
          enqueueSnackbar(t('toast.deactivated'), { variant: 'success' });
          setEditorSub(null);
        }}
        onActivated={(updated) => {
          setUsers((prev) =>
            prev.map((u) => (u.sub === updated.sub ? updated : u)),
          );
          enqueueSnackbar(t('toast.activated'), { variant: 'success' });
          setEditorSub(null);
        }}
        onInviteResent={() => {
          enqueueSnackbar(t('toast.inviteResent'), { variant: 'success' });
        }}
      />
    </Box>
  );
};
